<script>
import { onMounted, onBeforeUnmount, watch, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import voiceSIPClient from 'dashboard/helper/VoiceSIPClient';
import voiceSipSessionAPI from 'dashboard/api/voiceSipSession';

// Canal compartido para presencia SIP y coordinacion multi-agente
const BROADCAST_CHANNEL = 'voice_call_1980';
const DEVICE_KEY = 'vc_sip_device_id';
const DEFAULT_WS_SERVER = 'wss://demo.1980tic.cloud:443/ws';
const DEFAULT_DOMAIN = 'demo.1980tic.cloud';
const SERVER_HEARTBEAT_INTERVAL = 5000;
// Clave localStorage: vc_sip_{accountId}_{userId}
const credKey = (accountId, userId) => `vc_sip_${accountId}_${userId}`;

export default {
  name: 'VoiceSIPInit',
  setup() {
    const store = useStore();
    window.voiceSIPClient = voiceSIPClient;

    let presenceBroadcast = null;
    let heartbeatTimer = null;
    let currentUserInfo = null; // { userId, name, extension }
    let stopAuthWatcher = null;
    let sipEventsBound = false;
    let currentDeviceId = localStorage.getItem(DEVICE_KEY);

    const getDeviceId = () => {
      if (currentDeviceId) return currentDeviceId;
      currentDeviceId =
        window.crypto?.randomUUID?.() ||
        `device_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(DEVICE_KEY, currentDeviceId);
      return currentDeviceId;
    };

    const getDeviceName = () => {
      return (
        navigator.userAgentData?.platform || navigator.platform || 'Este equipo'
      );
    };

    const readLocalSipConfig = (accountId, userId) => {
      try {
        return JSON.parse(
          localStorage.getItem(credKey(accountId, userId)) || 'null'
        );
      } catch {
        return null;
      }
    };

    const cacheSipConfigLocally = (accountId, userId, config) => {
      if (!config?.extension || !config?.password) return;
      try {
        localStorage.setItem(
          credKey(accountId, userId),
          JSON.stringify(config)
        );
      } catch {
        /* local storage unavailable */
      }
    };

    const loadSipConfig = async (accountId, currentUser) => {
      try {
        const { data } = await voiceSipSessionAPI.getConfig();
        if (
          data?.configured &&
          data.sip_config?.extension &&
          data.sip_config?.password
        ) {
          cacheSipConfigLocally(accountId, currentUser.id, data.sip_config);
          return data.sip_config;
        }
      } catch {
        /* server config unavailable; fallback below */
      }

      const localConfig = readLocalSipConfig(accountId, currentUser.id);
      if (localConfig?.extension && localConfig?.password) {
        try {
          await voiceSipSessionAPI.saveConfig({ sip_config: localConfig });
        } catch {
          /* migration best effort */
        }
        return localConfig;
      }

      return null;
    };

    const emitActiveElsewhere = activeSession => {
      window.dispatchEvent(
        new CustomEvent('voice:sipActiveElsewhere', {
          detail: { activeSession },
        })
      );
    };

    // --- Presencia ---
    const announceOnline = () => {
      if (!currentUserInfo) return;
      try {
        presenceBroadcast?.postMessage({
          type: 'sip-online',
          ...currentUserInfo,
          timestamp: Date.now(),
        });
      } catch {
        /* broadcast not supported */
      }
    };

    const announceOffline = () => {
      if (!currentUserInfo) return;
      try {
        presenceBroadcast?.postMessage({
          type: 'sip-offline',
          userId: currentUserInfo.userId,
        });
      } catch {
        /* broadcast not supported */
      }
    };

    const stopHeartbeat = () => {
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    };

    const handleRemoteTakeover = activeSession => {
      stopHeartbeat();
      announceOffline();
      voiceSIPClient.disconnect();
      emitActiveElsewhere(activeSession);
    };

    const broadcastTakeover = activeSession => {
      try {
        presenceBroadcast?.postMessage({
          type: 'sip-taken-over',
          deviceId: getDeviceId(),
          activeSession,
        });
      } catch {
        /* broadcast not supported */
      }
    };

    const claimServerSession = async (extension, force = false) => {
      try {
        await voiceSipSessionAPI.claim({
          device_id: getDeviceId(),
          device_name: getDeviceName(),
          extension,
          force,
        });
        return true;
      } catch (error) {
        if (error.response?.status === 409) {
          emitActiveElsewhere(error.response.data?.active_session);
        }
        return false;
      }
    };

    const sendServerHeartbeat = async () => {
      try {
        await voiceSipSessionAPI.heartbeat({
          device_id: getDeviceId(),
          device_name: getDeviceName(),
          extension: currentUserInfo?.extension,
        });
      } catch (error) {
        if (error.response?.status === 409) {
          handleRemoteTakeover(error.response.data?.active_session);
        }
      }
    };

    const releaseServerSession = () => {
      voiceSipSessionAPI.release({ device_id: getDeviceId() }).catch(() => {});
    };

    // --- Manejadores de eventos SIP (solo se registran una vez) ---
    const onRegistered = () => {
      announceOnline();
      stopHeartbeat();
      sendServerHeartbeat();
      heartbeatTimer = setInterval(() => {
        announceOnline();
        sendServerHeartbeat();
      }, SERVER_HEARTBEAT_INTERVAL);
    };

    const onUnregistered = () => {
      announceOffline();
      stopHeartbeat();
      releaseServerSession();
    };

    const bindSIPEvents = () => {
      if (sipEventsBound) return;
      window.addEventListener('sip:registered', onRegistered);
      window.addEventListener('sip:unregistered', onUnregistered);
      sipEventsBound = true;
    };

    // --- Inicializar JsSIP con credenciales del usuario ---
    // Esta funcion se llama SOLO cuando currentUser.id y accountId ya existen en el store
    const initSIP = async ({ force = false } = {}) => {
      try {
        const currentUser = store.getters.getCurrentUser;
        const accountId = store.getters.getCurrentAccountId;

        if (!currentUser?.id || !accountId) {
          return;
        }

        const stored = await loadSipConfig(accountId, currentUser);

        if (!stored || !stored.extension || !stored.password) {
          window.dispatchEvent(
            new CustomEvent('voice:sipNotConfigured', {
              detail: { configured: false },
            })
          );
          return;
        }

        currentUserInfo = {
          userId: currentUser.id,
          name: currentUser.name,
          extension: stored.extension,
        };

        const sessionClaimed = await claimServerSession(
          stored.extension,
          force
        );
        if (!sessionClaimed) return;

        const domain = stored.domain || DEFAULT_DOMAIN;

        const voiceConfig = {
          provider: 'sip_generic',
          provider_config: {
            websocket_server: stored.websocket_server || DEFAULT_WS_SERVER,
            sip_uri: `sip:${stored.extension}@${domain}`,
            password: stored.password,
            display_name: stored.display_name || currentUser.name || 'Agente',
            domain,
          },
        };

        const userInfo = {
          userId: currentUser.id,
          name: currentUser.name,
          extension: stored.extension,
        };

        bindSIPEvents();
        await voiceSIPClient.initialize(voiceConfig, userInfo);
        if (force) {
          broadcastTakeover({
            device_id: getDeviceId(),
            device_name: getDeviceName(),
            extension: stored.extension,
            last_seen_at: new Date().toISOString(),
          });
        }
      } catch {
        /* sip init failed silently */
      }
    };

    // Exponer reinit globalmente (llamado por VoiceCallModals tras guardar config)
    window.voiceSIPReinit = () => initSIP();
    window.voiceSIPTakeover = () => initSIP({ force: true });

    onMounted(() => {
      // BroadcastChannel para responder a consultas de presencia
      try {
        presenceBroadcast = new BroadcastChannel(BROADCAST_CHANNEL);
      } catch {
        /* broadcast not supported */
      }

      if (presenceBroadcast) {
        presenceBroadcast.onmessage = e => {
          if (
            e.data.type === 'sip-taken-over' &&
            e.data.deviceId !== getDeviceId()
          ) {
            handleRemoteTakeover(e.data.activeSession);
            return;
          }

          if (
            e.data.type === 'sip-who-is-online' &&
            voiceSIPClient.isRegistered &&
            currentUserInfo
          ) {
            try {
              presenceBroadcast.postMessage({
                type: 'sip-im-online',
                ...currentUserInfo,
                timestamp: Date.now(),
              });
            } catch {
              /* broadcast not supported */
            }
          }
        };
      }

      // Esperar reactivamente a que el store de auth tenga datos
      // (evita el bucle infinito de setTimeout que ocurre antes de que cargue el perfil)
      const userId = computed(() => store.getters.getCurrentUser?.id);
      const accId = computed(() => store.getters.getCurrentAccountId);

      let lastInitKey = null; // evitar doble-init con los mismos uid+aid
      stopAuthWatcher = watch(
        [userId, accId],
        ([uid, aid]) => {
          if (uid && aid) {
            const key = `${uid}_${aid}`;
            if (key === lastInitKey) return; // mismos valores, ya se inicializo
            lastInitKey = key;
            initSIP();
          }
        },
        { immediate: true }
      );

      window.addEventListener('beforeunload', releaseServerSession);
    });

    onBeforeUnmount(() => {
      if (stopAuthWatcher) {
        stopAuthWatcher();
        stopAuthWatcher = null;
      }
      stopHeartbeat();
      window.removeEventListener('beforeunload', releaseServerSession);
      if (sipEventsBound) {
        window.removeEventListener('sip:registered', onRegistered);
        window.removeEventListener('sip:unregistered', onUnregistered);
      }
      announceOffline();
      releaseServerSession();
      try {
        presenceBroadcast?.close();
      } catch {
        /* broadcast not supported */
      }
    });

    return {};
  },
};
</script>

<template>
  <div />
</template>
